import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Card,
  CardContent,
  Container,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Loader from 'src/components/loader/loader';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { LocalStorage } from 'src/utils/storage/local-storage';
import { useToaster } from 'src/utils/toaster/toasterContext';
import ValidationTool from 'src/utils/validationHelper';
import landingImage from '../assets/app-image/landing.png';
import useResponsive from '../hooks/useResponsive';
import GoogleSignInComponent from './google-signin/googleSignIn.component';
import StarIcon from '@mui/icons-material/Star';
import BarChartIcon from '@mui/icons-material/BarChart';
import DevicesIcon from '@mui/icons-material/Devices';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const StyledRoot = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const Register = () => {
  const { t: translate } = useTranslation();
  const theme = useTheme();
  const mdUp = useResponsive('up', 'md');
  const smDown = useResponsive('down', 'sm');
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isIntegSocial = CommonHelper.IsIntegSocialDomain();
  const isCustomDomain = CommonHelper.CustomDomain();

  //State Variable

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const appLogo = CommonHelper.GetAppLogo(true);
  const [formSchema, setFormSchema] = useState({});
  const [formValidationErrors, setFormValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loader, setLoader] = useState(false);
  const { showToast } = useToaster();
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [timeZone, setTimeZone] = useState('UTC');
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const testimonials = [
    {
      text: 'This app made managing my brand’s social presence a breeze!',
      author: 'Albert Flores',
      company: 'Social Media Lead',
      logo: 'https://static2.brand2social.com/api/image-server/get-website-media?path=albert-flores.png',
    },
    {
      text: 'The AI DM feature is a game changer for customer engagement.',
      author: 'Robert Frost',
      company: 'Social Media Coordinator',
      logo: 'https://static2.brand2social.com/api/image-server/get-website-media?path=robert-frost.png',
    },
    {
      text: 'Auto Posting with AI saves us hours every week. Highly recommended!',
      author: 'Guy Hawkins',
      company: 'Marketing Manager',
      logo: 'https://static2.brand2social.com/api/image-server/get-website-media?path=guy-hawkins.png',
    },
  ];

  useEffect(() => {
    const localTimeZone = CommonHelper.GetLocalTimeZone();
    if (localTimeZone) {
      setTimeZone(localTimeZone);
    }
    let token = LocalStorage.getItem('token');
    if (token) {
      navigate('/new-post');
    } else {
      generateFormModelSchema();
    }
  }, []);

  useEffect(() => {
    if (!mdUp) return;
    const interval = setInterval(() => {
      setTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    }, 350000);
    return () => clearInterval(interval);
  }, [mdUp, testimonials.length]);

  const generateFormModelSchema = () => {
    let fields = [
      ValidationTool.GenerateSchema('firstName', translate('profile.general.first_name'), 'TEXT', true),
      ValidationTool.GenerateSchema('email', translate('profile.general.email'), 'EMAIL', true),
      ValidationTool.GenerateSchema('password', translate('profile.general.password'), 'TEXT', true),
    ];
    const schema = ValidationTool.GenerateSchemaModel(fields, translate);
    setFormSchema(schema);
  };

  const handleInputChange = (event) => {
    let value = event.target.value;
    let name = event.target.name;
    switch (name) {
      case 'email':
        setEmail(value);
        break;
      case 'password':
        setPassword(value);
        break;
      case 'firstName':
        setFirstName(value);
        break;
      case 'lastName':
        setLastName(value);
        break;
      default:
        break;
    }
  };

  const handleOnBlur = (event) => {
    let fieldName = event.target.name;
    let fieldValue = event.target.value;
    let validationErrors = { ...formValidationErrors };
    let message = ValidationTool.ValidateInput(formSchema, fieldValue, fieldName);
    if (message) validationErrors[fieldName] = message;
    else validationErrors[fieldName] = '';
    setFormValidationErrors(validationErrors);
  };

  const handleRegister = () => {
    let registerForm = {
      firstName: firstName,
      email: email,
      password: password,
      device_type: isMobile ? CommonHelper.DeviceType.Mobile : CommonHelper.DeviceType.Desktop,
    };
    let { formValidationErrors, isValidForm } = ValidationTool.FormValidation(formSchema, registerForm);
    if (isValidForm) handleCreateNewUser();
    else setFormValidationErrors(formValidationErrors);
  };

  const handleLogin = () => {
    let loginForm = {
      email: 'demo@brand2social.com',
      password: 'demo1234',
    };
    let postProps = {
      url: userModuleURL.login,
      body: loginForm,
      successCallback: loginSuccessCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };

  const handleCreateNewUser = () => {
    setLoading(true);
    let registerForm = {
      first_name: firstName,
      last_name: lastName,
      email: email,
      password: password,
      timezone: timeZone,
      is_custom_domain :isCustomDomain,
    };
    let postProps = {
      url: userModuleURL.register,
      body: registerForm,
      successCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };
  const loginSuccessCallback = (data, message) => {
    message && showToast(message);
    CommonHelper.AppLoginCall(data);
  };
  const successCallback = (data, message) => {
    message && showToast(message);
    data['is_register'] = true;
    CommonHelper.AppLoginCall(data);
  };
  const failureCallback = (message) => {
    message && showToast(message, 'e');
    setLoading(false);
  };
  const navigateToLogin = () => {
    navigate('/login');
  };
  const applyDemoCredential = () => {
    setDemoLoading(true);
    handleLogin();
  };

  return (
    <>
      {loader && <Loader />}
      <Helmet>
        <title> {translate('common.register')} </title>
      </Helmet>
      <StyledRoot>
        {mdUp && (
          <Box
            sx={{
              flex: 1,
              minHeight: '100vh',
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, #fff 100%)`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              position: 'relative',
              px: 6,
              py: 4,
              boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
              borderTopRightRadius: 32,
              borderBottomRightRadius: 32,
            }}
          >
            <img
              src={appLogo}
              style={{ height: 56, marginBottom: 36, filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.10))' }}
              alt="logo"
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 2,
                letterSpacing: 1,
                textShadow: '0 2px 8px rgba(0,0,0,0.10)',
                textAlign: 'center',
              }}
            >
              {translate('register.welcome_to')} {CommonHelper.GetAppName()}
            </Typography>
            {/* Feature Highlights */}
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, mb: 2, textAlign: 'center', color: '#fff' }}
            >
              {translate('register.feature_highlights')}
            </Typography>
            <Stack direction="row" spacing={3} sx={{ mb: 5, mt: 2, justifyContent: 'center', width: '100%' }}>
              <Box sx={{ textAlign: 'center', px: 2, minWidth: 120 }}>
                <StarIcon
                  sx={{ fontSize: 44, color: '#fffde7', mb: 1, filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.10))' }}
                />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {translate('register.schedule_posts')}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85, fontSize: 15 }}>
                  {translate('register.schedule_posts_desc')}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', px: 2, minWidth: 120 }}>
                <BarChartIcon
                  sx={{ fontSize: 44, color: '#fffde7', mb: 1, filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.10))' }}
                />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {translate('register.analytics_dashboard')}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85, fontSize: 15 }}>
                  {translate('register.analytics_dashboard_desc')}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', px: 2, minWidth: 120 }}>
                <DevicesIcon
                  sx={{ fontSize: 44, color: '#fffde7', mb: 1, filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.10))' }}
                />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {translate('register.multi_platform')}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85, fontSize: 15 }}>
                  {translate('register.multi_platform_desc')}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', px: 2, minWidth: 120 }}>
                <SmartToyIcon
                  sx={{ fontSize: 44, color: '#fffde7', mb: 1, filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.10))' }}
                />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {translate('register.ai_dm')}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85, fontSize: 15 }}>
                  {translate('register.ai_dm_desc')}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', px: 2, minWidth: 120 }}>
                <AutoAwesomeMotionIcon
                  sx={{ fontSize: 44, color: '#fffde7', mb: 1, filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.10))' }}
                />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {translate('register.ai_auto_posting')}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85, fontSize: 15 }}>
                  {translate('register.ai_auto_posting_desc')}
                </Typography>
              </Box>
            </Stack>
            {/* Testimonials Carousel */}
            <Box sx={{ width: '100%', maxWidth: 480, mb: 5 }}>
              <Box
                id="testimonial-carousel"
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 3,
                  background: 'rgba(255,255,255,0.13)',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
                  p: 3,
                  minHeight: 120,
                  p: 8,
                }}
              >
                {testimonials[testimonialIndex] && (
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <img
                      src={testimonials[testimonialIndex].logo}
                      alt="user image"
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        background: '#fff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                      }}
                    />
                    <Box>
                      <Typography
                        variant="body1"
                        sx={{ fontStyle: 'italic', color: '#fff', fontWeight: 500, mb: 0.5, textAlign: 'left' }}
                      >
                        “{testimonials[testimonialIndex].text}”
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.secondary.dark,
                          fontWeight: 700,
                          mt: 1,
                          display: 'block',
                          letterSpacing: 0.5,
                          textAlign: 'left',
                        }}
                      >
                        — {testimonials[testimonialIndex].author}, {testimonials[testimonialIndex].company}
                      </Typography>
                    </Box>
                  </Stack>
                )}
                {/* Carousel Arrows */}
                <IconButton
                  aria-label="Previous testimonial"
                  onClick={() => setTestimonialIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#fff',
                    background: 'rgba(0,0,0,0.15)',
                    '&:hover': { background: 'rgba(0,0,0,0.25)' },
                    zIndex: 1,
                  }}
                  size="small"
                >
                  <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label="Next testimonial"
                  onClick={() => setTestimonialIndex((prev) => (prev + 1) % testimonials.length)}
                  sx={{
                    position: 'absolute',
                    right: 18,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#fff',
                    background: 'rgba(0,0,0,0.15)',
                    '&:hover': { background: 'rgba(0,0,0,0.25)' },
                    zIndex: 1,
                    mr: -2,
                  }}
                  size="small"
                >
                  <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            {/* Getting Started Steps */}
            <Box
              sx={{
                mb: 5,
                width: '100%',
                maxWidth: 380,
                background: 'rgba(255,255,255,0.10)',
                borderRadius: 2,
                p: 2.5,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, mb: 1, color: '#fff', letterSpacing: 0.5, textAlign: 'center' }}
              >
                {translate('register.get_started_steps')}
              </Typography>
              <Stack spacing={1.2}>
                <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500, fontSize: 16, textAlign: 'center' }}>
                  <b>1.</b> {translate('register.register_your_account')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500, fontSize: 16, textAlign: 'center' }}>
                  <b>2.</b> {translate('register.connect_social_channels')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500, fontSize: 16, textAlign: 'center' }}>
                  <b>3.</b> {translate('register.start_scheduling_posts')}
                </Typography>
              </Stack>
            </Box>
          </Box>
        )}
        <Container maxWidth="sm" sx={{ display: 'flex', alignItems: 'flex-start', height: '100vh', overflowY: 'auto', pt: 2 }}>
          <Box sx={{ width: '100%' }}>
            {smDown ? (
              <Box sx={{ p: 2 }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <img src={appLogo} style={{ height: 32, marginBottom: 16 }} alt="logo" />
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    {translate('affiliate.register_in')} {CommonHelper.GetAppName()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {translate('register.welcome_to')} {CommonHelper.GetAppName()}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <Stack spacing={3} sx={{ mb: 5 }}>
                  <TextField
                    error={formValidationErrors?.firstName}
                    color={firstName === '' ? 'error' : 'success'}
                    name="firstName"
                    label={translate('profile.general.first_name')}
                    onChange={handleInputChange}
                    value={firstName}
                    helperText={formValidationErrors?.firstName}
                    onBlur={handleOnBlur}
                  />
                  <TextField
                    name="lastName"
                    label={translate('profile.general.last_name')}
                    onChange={handleInputChange}
                    value={lastName}
                  />
                  <TextField
                    error={formValidationErrors?.email}
                    color={email === '' ? 'error' : 'success'}
                    name="email"
                    label={translate('profile.general.email')}
                    onChange={handleInputChange}
                    value={email}
                    helperText={formValidationErrors?.email}
                    onBlur={handleOnBlur}
                  />
                  <TextField
                    error={formValidationErrors?.password}
                    color={password === '' ? 'error' : 'success'}
                    name="password"
                    label={translate('profile.general.password')}
                    type={showPassword ? 'text' : 'password'}
                    onChange={handleInputChange}
                    onBlur={handleOnBlur}
                    value={password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                            {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    helperText={formValidationErrors?.password}
                  />
                </Stack>
                <LoadingButton
                  fullWidth
                  size="large"
                  type="submit"
                  loading={loading}
                  variant="contained"
                  onClick={handleRegister}
                >
                  {translate('common.register')}
                </LoadingButton>
                {!isIntegSocial && !isCustomDomain && (
                  <>
                    <Stack
                      style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20px' }}
                    >
                      <LoadingButton
                        fullWidth
                        size="large"
                        loading={demoLoading}
                        variant="outlined"
                        color="info"
                        startIcon={<StarIcon />}
                        onClick={applyDemoCredential}
                        sx={{
                          borderWidth: 2,
                          borderColor: theme.palette.info.main,
                          fontWeight: 'bold',
                          letterSpacing: 1,
                          backgroundColor: '#e3f2fd',
                          color: theme.palette.info.dark,
                          '&:hover': {
                            backgroundColor: '#bbdefb',
                          },
                        }}
                      >
                        {translate('register.use_demo_account')}
                      </LoadingButton>
                    </Stack>
                    <Typography
                      variant="caption"
                      align="center"
                      display="block"
                      sx={{ mt: 1, color: 'info.main', fontWeight: 500 }}
                    >
                      {translate('register.try_demo_account')}
                    </Typography>
                  </>
                )}
                {!isCustomDomain &&<Stack style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20px' }}>
                  <GoogleSignInComponent
                    handleLoginSuccess={successCallback}
                    googleButtonText={translate('register.register_via_google')}
                  />
                </Stack>}
                <Divider sx={{ my: 3 }} />
                <Typography variant="body2" align="center">
                  {translate('register.already_have_an_account')}?{' '}
                  <Link
                    onClick={navigateToLogin}
                    sx={{
                      color: 'primary.main',
                      fontWeight: 'bold',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                  >
                    {translate('register.login_here')}
                  </Link>
                </Typography>
              </Box>
            ) : (
              <Card elevation={8} sx={{ borderRadius: 4, p: { xs: 2, sm: 4 } }}>
                <CardContent>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <img src={appLogo} style={{ height: 40, marginBottom: 16 }} alt="logo" />
                    <Typography variant="h4" fontWeight={700} gutterBottom>
                      {translate('affiliate.register_in')} {CommonHelper.GetAppName()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {translate('register.welcome_to')} {CommonHelper.GetAppName()}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  <Stack spacing={3} sx={{ mb: 5 }}>
                    <TextField
                      error={formValidationErrors?.firstName}
                      color={firstName === '' ? 'error' : 'success'}
                      name="firstName"
                      label={translate('profile.general.first_name')}
                      onChange={handleInputChange}
                      value={firstName}
                      helperText={formValidationErrors?.firstName}
                      onBlur={handleOnBlur}
                    />
                    <TextField
                      name="lastName"
                      label={translate('profile.general.last_name')}
                      onChange={handleInputChange}
                      value={lastName}
                    />
                    <TextField
                      error={formValidationErrors?.email}
                      color={email === '' ? 'error' : 'success'}
                      name="email"
                      label={translate('profile.general.email')}
                      onChange={handleInputChange}
                      value={email}
                      helperText={formValidationErrors?.email}
                      onBlur={handleOnBlur}
                    />
                    <TextField
                      error={formValidationErrors?.password}
                      color={password === '' ? 'error' : 'success'}
                      name="password"
                      label={translate('profile.general.password')}
                      type={showPassword ? 'text' : 'password'}
                      onChange={handleInputChange}
                      onBlur={handleOnBlur}
                      value={password}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                              {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      helperText={formValidationErrors?.password}
                    />
                  </Stack>
                  <LoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    loading={loading}
                    variant="contained"
                    onClick={handleRegister}
                  >
                    {translate('common.register')}
                  </LoadingButton>
                  {!isIntegSocial && !isCustomDomain &&(
                    <>
                      <Stack
                        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20px' }}
                      >
                        <LoadingButton
                          fullWidth
                          size="large"
                          loading={demoLoading}
                          variant="outlined"
                          color="info"
                          startIcon={<StarIcon />}
                          onClick={applyDemoCredential}
                          sx={{
                            borderWidth: 2,
                            borderColor: theme.palette.info.main,
                            fontWeight: 'bold',
                            letterSpacing: 1,
                            backgroundColor: '#e3f2fd',
                            color: theme.palette.info.dark,
                            '&:hover': {
                              backgroundColor: '#bbdefb',
                            },
                          }}
                        >
                          {translate('register.use_demo_account')}
                        </LoadingButton>
                      </Stack>
                      <Typography
                        variant="caption"
                        align="center"
                        display="block"
                        sx={{ mt: 1, color: 'info.main', fontWeight: 500 }}
                      >
                        {translate('register.try_demo_account')}
                      </Typography>
                    </>
                  )}
                 {!isCustomDomain && <Stack style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20px' }}>
                    <GoogleSignInComponent
                      handleLoginSuccess={successCallback}
                      googleButtonText={translate('register.register_via_google')}
                    />
                  </Stack>}
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="body2" align="center">
                    {translate('register.already_have_an_account')}?{' '}
                    <Link
                      onClick={navigateToLogin}
                      sx={{
                        color: 'primary.main',
                        fontWeight: 'bold',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }}
                    >
                      {translate('register.login_here')}
                    </Link>
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        </Container>
      </StyledRoot>
    </>
  );
};
export default Register;
